import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useId,
} from "react";
import { Package } from "lucide-react";
import InventoryController from "@/modules/inventory/InventoryController";
import { useHasInventory } from "@/modules/inventory/useHasInventory";
import { Button } from "@/components/ui/button";
import {
  ItemUnit,
  ITEM_UNIT_LABEL,
  WeightUnit,
  WeightUnitEnum,
  WEIGHT_UNIT_ABBREVIATION,
  DimensionUnit,
  DimensionUnitSchema,
  DIMENSION_UNIT_ABBREVIATION,
  DIMENSION_UNIT_LABEL,
  CreateItemParams,
  UpdateItemParams,
  Asset,
  ItemMargin,
  HydratedItem,
  ItemCost,
  MarginTypeEnum,
  PriceModel,
  CostingMethodEnum,
  ItemTypeEnum,
  getDefaultCostingMethod,
  HydratedOrganizationFile,
  ItemUnitSchema,
  PriceModelInputMetadataEntry,
  ItemEditorViewContext,
} from "@forgepoint/shared";
import {
  Loader,
  DraftingCompass,
  Info,
  NotebookPen,
  Vault,
  Lock,
  MessageSquare,
  History,
} from "lucide-react";
import { toast } from "sonner";
import {
  TwoColumnLayout,
  TwoColumnLayoutProps,
} from "@/components/TwoColumnLayout";
import { TwoColumnSheet } from "@/components/TwoColumnSheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ItemEditorConflictBanner } from "./ItemEditorConflictBanner";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  draftConflictKeys,
  draftDirtyKeys,
  draftFormData,
  isSameValue,
  recordFieldEdits,
  useItemDraftsStore,
} from "./itemDraftsStore";
import { fetchApi } from "@/lib/api";
import BOMWrapper from "./BOMWrapper";
import type { QuoteCurrencyFields } from "@/lib/quoteCurrency";
import {
  PriceModelSummary,
  usePriceModelCosts,
} from "./PriceModelOutputsFooter";
import { useItemsStore } from "./itemsStore";
import { useItemRevalidationPoll } from "./useItemRevalidationPoll";
import { resolveDisplayItem } from "./utils";
import {
  buildCreateFormData,
  getFormDataFromItem,
  ItemFormData,
} from "./itemFormData";
import { useFeatureFlag } from "@/store/featureFlagStore";
import { useItemSettingsStore } from "@/store/itemSettingsStore";
import { useViewContextStore } from "@/store/viewContextStore";
import { AssemblyTreeSidebar } from "./AssemblyTreeSidebar";
import { ReviewStatusIndicator } from "@/modules/quotes/components/ReviewStatusIndicator";
import { AgentWorkingBadge } from "@/modules/standardAgent/components/AgentWorkingBadge";
import { ItemCostBreak } from "./ItemCostBreak";
import { ItemAssociatedQuotes } from "./ItemAssociatedQuotes";
import { ItemBriefingTab } from "./ItemBriefingTab";
import { ItemDrawingsTab } from "./ItemDrawingsTab";
import { ItemVaultTab } from "./ItemVaultTab";
import { ItemCommsMessagesTab } from "./ItemCommsMessagesTab";
import { ItemHistoryTab } from "./ItemHistoryTab";
import { useFetchItemEvents } from "./useFetchItemEvents";
import type { ItemFieldTarget } from "./itemHistoryLabels";
import { ItemMarginEditor } from "./ItemMarginEditor";
import { createId } from "@paralleldrive/cuid2";
import { usePriceModelsStore } from "@/modules/priceModels/priceModelsStore";
import Decimal from "decimal.js";
import { JSONSchemaForm } from "@/components/JSONSchemaForm";
import {
  JSONValue,
  JSONObject,
  isJSONObject,
} from "@/components/jsonSchemaForm/types";
import { PriceModelInputFieldTemplate } from "./PriceModelInputFieldTemplate";
import type { PriceModelInputFormContext } from "./PriceModelInputFieldTemplate";
import { metadataPathKey } from "@forgepoint/shared";
import {
  alignBaselineRows,
  collectChangedLeafPaths,
  indexMetadataByPath,
  readValueAtPath,
  setValueAtPath,
} from "./priceModelInputMetadata";
import { FieldLabel } from "@/components/jsonSchemaForm/FieldLabel";
import { JSONSchema7, JSONSchema7Definition } from "json-schema";
import { CustomFieldsSection } from "@/components/customFields/CustomFieldsSection";
import {
  getConversionUnitOptions,
  getItemUnitOptions,
} from "./itemUnitOptions";
import { ItemUnitConversionsEditor } from "./ItemUnitConversionsEditor";

/**
 * A settings-row style form field with label on the left and control on the right.
 * @param props - FormRow properties
 * @returns Horizontal form row element
 */
function FormRow({
  label,
  htmlFor,
  required,
  children,
  note,
  wide,
  description,
  anchor,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  children: React.ReactNode;
  note?: React.ReactNode;
  wide?: boolean;
  anchor?: string;
  /**
   * When set, the label reveals this text in a click popover (via the shared
   * FieldLabel) instead of printing it as a `note` below the row — same
   * affordance as the price-model input fields.
   */
  description?: string;
}) {
  return (
    <div className="py-2.5" data-field-anchor={anchor}>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5">
        {description ? (
          <FieldLabel
            id={htmlFor}
            label={label}
            required={required}
            description={description}
          />
        ) : (
          <Label
            htmlFor={htmlFor}
            className="text-sm font-medium leading-tight"
          >
            {label}
            {required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
        )}
        <div
          className={cn(
            "min-w-[120px] flex-1",
            wide ? "max-w-[320px]" : "max-w-[200px]",
          )}
        >
          {children}
        </div>
      </div>
      {note}
    </div>
  );
}

/**
 * Builds the effective default inputs for a price model by merging
 * schema-level defaults with the explicit defaultInputs column.
 * @param pm - The price model
 * @returns Merged defaults as a JSONValue (schema defaults as base, defaultInputs overrides)
 */
function getEffectiveDefaults(pm: PriceModel): JSONValue {
  const properties = pm.inputSchema?.properties;
  if (!properties || typeof properties !== "object") {
    return (pm.defaultInputs as JSONValue) ?? {};
  }

  const schemaDefaults: Record<string, unknown> = {};
  for (const [key, propSchema] of Object.entries(
    properties as Record<string, Record<string, unknown>>,
  )) {
    if ("default" in propSchema) {
      schemaDefaults[key] = propSchema.default;
    }
  }

  return { ...schemaDefaults, ...pm.defaultInputs } as JSONValue;
}

/** A read-only, model-computed field extracted from a price model input schema. */
interface CalculationField {
  key: string;
  label: string;
  description?: string;
  suffix?: string;
}

/**
 * Splits a price model input schema into the editable input fields and the
 * read-only computed (`fpCalculation`) fields, which are displayed separately.
 * @param schema - The price model input schema (may be null)
 * @returns The schema with calculation fields removed, plus the extracted calculation fields
 */
function splitCalculationFields(schema: JSONSchema7 | null): {
  inputSchema: JSONSchema7 | null;
  calculationFields: CalculationField[];
} {
  if (!schema?.properties) {
    return { inputSchema: schema, calculationFields: [] };
  }

  const calculationFields: CalculationField[] = [];
  const inputProperties: Record<string, JSONSchema7Definition> = {};
  for (const [key, prop] of Object.entries(schema.properties)) {
    if (typeof prop === "object" && prop.format === "fpCalculation") {
      const suffix = "suffix" in prop ? prop.suffix : undefined;
      calculationFields.push({
        key,
        label: prop.title ?? key,
        description:
          typeof prop.description === "string" ? prop.description : undefined,
        suffix: typeof suffix === "string" ? suffix : undefined,
      });
    } else {
      inputProperties[key] = prop;
    }
  }

  if (calculationFields.length === 0) {
    return { inputSchema: schema, calculationFields: [] };
  }

  const inputSchema: JSONSchema7 = {
    ...schema,
    properties: inputProperties,
    required: schema.required?.filter(
      (name) => !calculationFields.some((field) => field.key === name),
    ),
  };
  return { inputSchema, calculationFields };
}

// Draft key for create mode, where no server item exists yet.
const CREATE_DRAFT_ID = "__create__";

/** An in-flight (or, in create mode, accumulating) asset set for one item. */
interface PendingAssetState {
  assets: Asset[];
  primaryId: string | null;
}

/** Create mode starts with an empty pending set; edit mode renders server truth until a save is in flight. */
function initialPendingAssets(
  mode: "create" | "edit",
): Map<string, PendingAssetState> {
  return mode === "create"
    ? new Map([[CREATE_DRAFT_ID, { assets: [], primaryId: null }]])
    : new Map();
}

/** Create mode starts with an empty pending set; edit mode renders server truth until a save is in flight. */
function initialPendingVaultFiles(
  mode: "create" | "edit",
): Map<string, HydratedOrganizationFile[]> {
  return mode === "create" ? new Map([[CREATE_DRAFT_ID, []]]) : new Map();
}

/**
 * Props for the ItemEditor component.
 * Supports two modes:
 * - "create": For creating new items (no itemId needed)
 * - "edit": For editing existing items (requires itemId, fetches data internally)
 */
type ItemEditorProps =
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      itemId?: undefined;
      onSave: (data: CreateItemParams) => Promise<true | string>;
      mode: "create";
      /** Edit-mode only; a new item is never viewed from a finalized quote. */
      quotedItem?: undefined;
      /** Edit-mode only; a new item is never priced in a quote's currency. */
      currencyContext?: undefined;
      /** Seeds the form fields when the sheet opens in create mode. */
      initialValues?: Partial<CreateItemParams>;
      quantities?: string[];
      onBomUpdated?: () => void;
      defaultTab?: "details" | "bom";
      /** When true, renders inline filling the parent container instead of a full-screen sheet. */
      inline?: boolean;
      /** Inline-only: toggles the surrounding chat panel; renders a Hide/Show Chat control in the header. */
      onToggleChat?: () => void;
      /** Inline-only: whether the surrounding chat panel is collapsed. */
      isChatCollapsed?: boolean;
      /** Inline-only: true while the agent turn is running; shows a working badge. */
      isAgentWorking?: boolean;
    }
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      itemId: string;
      onSave: (data: UpdateItemParams) => Promise<true | string>;
      mode: "edit";
      /** The finalized quote's frozen snapshot of this item; a live item newer than its snapshot shows a changed-since-finalized notice. */
      quotedItem?: HydratedItem;
      /** The quote's currency fields; a multi-currency quote adds its currency price to the BOM pricing table. */
      currencyContext?: QuoteCurrencyFields;
      /** Create-mode only; the edit form is seeded from the item being edited. */
      initialValues?: undefined;
      quantities?: string[];
      onBomUpdated?: () => void;
      defaultTab?: "details" | "bom";
      /** When true, renders inline filling the parent container instead of a full-screen sheet. */
      inline?: boolean;
      /** Inline-only: toggles the surrounding chat panel; renders a Hide/Show Chat control in the header. */
      onToggleChat?: () => void;
      /** Inline-only: whether the surrounding chat panel is collapsed. */
      isChatCollapsed?: boolean;
      /** Inline-only: true while the agent turn is running; shows a working badge. */
      isAgentWorking?: boolean;
    };

/** Maps the left tab's value to the agent-facing view-context tab name. */
const VIEW_TAB_BY_LEFT_TAB: Record<string, ItemEditorViewContext["activeTab"]> =
  {
    drawings: "drawings",
    briefing: "briefing",
    vault: "vault",
    comms: "evidence",
  };

/**
 * Creates or edits an item, rendering as a full-screen sheet by default or
 * inline within its parent container when `inline` is set.
 * In edit mode, fetches the item data internally when opened.
 * Pulls item data directly from the store to stay in sync with updates.
 * Limits the part number length and unit options to the org's item settings.
 * @param props - The item to edit, editor mode, and save handlers
 * @returns The item editor
 */
export default function ItemEditor({
  open,
  onOpenChange,
  itemId,
  onSave,
  mode,
  quotedItem,
  currencyContext,
  quantities: propQuantities,
  onBomUpdated,
  defaultTab,
  initialValues,
  inline = false,
  onToggleChat,
  isChatCollapsed = false,
  isAgentWorking = false,
}: ItemEditorProps) {
  const { fetchItem, markItemsReviewed, updateItem } = useItemsStore();
  const epicorPrimitiveSupportEnabled = useFeatureFlag(
    "epicor-primitive-support",
  );

  // Price model support. Archived models are fetched so a linked one still
  // renders its inputs, but they are filtered out of the picker options.
  const priceModelsMap = usePriceModelsStore((state) => state.priceModels);
  const fetchPriceModels = usePriceModelsStore(
    (state) => state.fetchPriceModels,
  );
  const priceModelsList = useMemo(
    () => Array.from(priceModelsMap.values()).filter((pm) => !pm.archivedAt),
    [priceModelsMap],
  );
  const [activeTab, setActiveTab] = useState<string>(defaultTab ?? "details");

  useEffect(() => {
    if (open && defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [open, defaultTab]);

  const [selectedPriceModelId, setSelectedPriceModelId] = useState<
    string | null
  >(null);
  // Per-input provenance written by the agent, for display only. The server
  // owns it, so this is never sent back on a save.
  const [priceModelInputMetadata, setPriceModelInputMetadata] = useState<
    PriceModelInputMetadataEntry[]
  >([]);
  // The inputs the sparkles describe: whatever was last adopted from the server
  // or applied on a price model switch. Edits are measured against this rather
  // than against the previous keystroke, so undoing one restores its sparkle.
  const [baselinePriceModelInputs, setBaselinePriceModelInputs] =
    useState<JSONValue>({});
  // Signature of the persisted metadata already adopted into editor state.
  const syncedMetadataSignatureRef = useRef<string | null>(null);

  // Pull root item from store — the assembly root that the sheet was opened with
  const storeItem = useItemsStore((state) =>
    open && mode === "edit" && itemId
      ? state.getHydratedItem(itemId)
      : undefined,
  );
  const itemLoadingState = useItemsStore((state) =>
    open && mode === "edit" && itemId
      ? state.getItemLoadingState(itemId)
      : null,
  );

  const isLoading = itemLoadingState?.isLoadingGet ?? false;
  const fetchError = itemLoadingState?.getError ?? null;

  // Catches writers with no other signal (propagation, ERP sync, other tabs).
  useItemRevalidationPoll(open && mode === "edit" ? itemId : null);

  const [isSaving, setIsSaving] = useState(false);
  // Version of the displayed item the draft was last reconciled against.
  const lastReconciledRef = useRef<{
    itemId: string;
    updatedAtMs: number;
  } | null>(null);
  // Item ids this editor opened drafts for, so closing can clear them all.
  const openedDraftIdsRef = useRef<Set<string>>(new Set());
  // The refreshed server item awaiting a user decision while the form is dirty.
  const [conflictItem, setConflictItem] = useState<HydratedItem | null>(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  // Instant-save overlay for assets, keyed by item id (a save in flight on
  // one assembly component must not shadow another's). In create mode the
  // CREATE_DRAFT_ID entry IS the pending set.
  const [pendingAssetsByItem, setPendingAssetsByItemState] = useState(
    initialPendingAssets(mode),
  );
  const pendingAssetsRef = useRef(pendingAssetsByItem);
  const writePendingAssets = useCallback(
    (next: Map<string, PendingAssetState>) => {
      pendingAssetsRef.current = next;
      setPendingAssetsByItemState(next);
    },
    [],
  );
  /** Sets (or, with null, clears) the pending asset set for one item. */
  const setPendingAssetsFor = useCallback(
    (targetId: string, next: PendingAssetState | null) => {
      const map = new Map(pendingAssetsRef.current);
      if (next) {
        map.set(targetId, next);
      } else {
        map.delete(targetId);
      }
      writePendingAssets(map);
    },
    [writePendingAssets],
  );
  // Chains asset saves so concurrent uploads accumulate and commit in order,
  // plus the latest queued save per item so its tail clears only that overlay.
  const assetSaveQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const latestAssetSaveRef = useRef<Map<string, Promise<unknown>>>(new Map());
  const [selectedAssetIndex, setSelectedAssetIndex] = useState(0);
  const [activeLeftTab, setActiveLeftTab] = useState("drawings");
  const estimationStationEnabled = useFeatureFlag("estimation-station");
  const isCommsMessagesEnabled = useFeatureFlag("item-comms-messages");
  const isItemHistoryEnabled = useFeatureFlag("item-history");

  const hasInventory = useHasInventory(open && mode === "edit");

  // A flagged trigger unmounts when its flag flips off; fall back to drawings.
  useEffect(() => {
    if (
      (!estimationStationEnabled && activeLeftTab === "vault") ||
      (!isItemHistoryEnabled && activeLeftTab === "history") ||
      (!hasInventory && activeLeftTab === "inventory")
    ) {
      setActiveLeftTab("drawings");
    }
  }, [
    estimationStationEnabled,
    isItemHistoryEnabled,
    hasInventory,
    activeLeftTab,
  ]);
  // Instant-save overlay for vault links, keyed by item id: a save in flight on
  // one assembly component must not shadow another component's links. In create
  // mode the CREATE_DRAFT_ID entry IS the pending set.
  const [pendingVaultFilesByItem, setPendingVaultFilesByItemState] = useState(
    initialPendingVaultFiles(mode),
  );
  // Ref mirror written with the state, so rapid pin/unpin clicks read the
  // latest links before React re-renders.
  const pendingVaultFilesRef = useRef(pendingVaultFilesByItem);
  const writePendingVaultFiles = useCallback(
    (next: Map<string, HydratedOrganizationFile[]>) => {
      pendingVaultFilesRef.current = next;
      setPendingVaultFilesByItemState(next);
    },
    [],
  );
  /** Sets (or, with null, clears) the pending links for one item. */
  const setPendingVaultFilesFor = useCallback(
    (targetId: string, files: HydratedOrganizationFile[] | null) => {
      const next = new Map(pendingVaultFilesRef.current);
      if (files) {
        next.set(targetId, files);
      } else {
        next.delete(targetId);
      }
      writePendingVaultFiles(next);
    },
    [writePendingVaultFiles],
  );
  // Chains vault-link saves so concurrent PUTs can't commit out of click order,
  // plus the latest queued save per item so its tail clears only that overlay.
  const vaultLinkSaveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const latestVaultSaveRef = useRef<Map<string, Promise<void>>>(new Map());
  const [enableCostBreaks, setEnableCostBreaks] = useState(false);
  const [enableItemMargins, setEnableItemMargins] = useState(false);
  const itemSettings = useItemSettingsStore((state) => state.settings);
  const fetchItemSettings = useItemSettingsStore(
    (state) => state.fetchSettings,
  );
  const customFieldsSchema = itemSettings?.customFieldsSchema ?? null;
  const maxPartNumberLength = itemSettings?.maxPartNumberLength ?? null;
  const allowedItemUnits = itemSettings?.allowedItemUnits ?? null;
  const enableAllowance = itemSettings?.enableAllowance ?? false;

  // Stash UNIT_COST data so users can toggle to BOM and back without losing it
  const unitCostStashRef = useRef<{
    costBreaks: ItemCost[];
    itemMargins: ItemMargin[];
    enableCostBreaks: boolean;
    enableItemMargins: boolean;
  } | null>(null);
  // Stash BOM-specific margins so switching to UNIT_COST and back preserves them
  const bomMarginsStashRef = useRef<ItemMargin[] | null>(null);
  const [selectedTreeItemId, setSelectedTreeItemId] = useState<string | null>(
    null,
  );

  const rootItem = mode === "edit" && storeItem ? storeItem : null;

  const isAssembly =
    rootItem?.costingMethod === CostingMethodEnum.BOM &&
    rootItem.components &&
    rootItem.components.length > 0;

  // Resolve the currently displayed item from the tree
  const selectedTreeComponent = useMemo(() => {
    if (!isAssembly || !selectedTreeItemId || !rootItem) {
      return null;
    }
    if (selectedTreeItemId === rootItem.id) {
      return null;
    }
    return findItemInTree(rootItem, selectedTreeItemId);
  }, [isAssembly, selectedTreeItemId, rootItem]);

  // A component's own store entry can be fresher than the root's embedded
  // copy (live refresh refetches mutated items by their own id).
  const selectedComponentOwnEntry = useItemsStore((state) =>
    selectedTreeItemId
      ? state.hydratedItems.get(selectedTreeItemId)
      : undefined,
  );

  // `item` is what the editor displays — the selected component or the root
  const item = selectedTreeComponent
    ? resolveDisplayItem(selectedTreeComponent, selectedComponentOwnEntry)
    : rootItem;
  const isViewingComponent = selectedTreeComponent !== null;
  const isSynced = Boolean(item?.externalMappings?.length);
  const lastIntegrationUpdate =
    item?.externalMappings?.reduce<Date | null>((latest, mapping) => {
      if (!latest || mapping.syncedAt.getTime() > latest.getTime()) {
        return mapping.syncedAt;
      }
      return latest;
    }, null) ?? null;
  const isReadOnly = mode === "edit" && isSynced;
  const quotedSnapshot =
    quotedItem && item ? findItemInTree(quotedItem, item.id) : null;
  const isChangedSinceQuoted = Boolean(
    item &&
      quotedSnapshot &&
      item.updatedAt.getTime() > quotedSnapshot.updatedAt.getTime(),
  );

  useFetchItemEvents(
    isItemHistoryEnabled && open && mode === "edit" ? (item?.id ?? null) : null,
  );

  // ---- Draft overlay: server truth plus only the user's edited keys ----
  const draftId = mode === "edit" ? (item?.id ?? null) : CREATE_DRAFT_ID;
  const draft = useItemDraftsStore((state) =>
    draftId ? state.draftsByItemId.get(draftId) : undefined,
  );
  const ensureDraft = useItemDraftsStore((state) => state.ensureDraft);
  const setDraftValue = useItemDraftsStore((state) => state.setDraftValue);
  const rebaseDraft = useItemDraftsStore((state) => state.rebaseDraft);
  const clearDraft = useItemDraftsStore((state) => state.clearDraft);
  const serverForm = useMemo(
    () =>
      mode === "edit" && item
        ? getFormDataFromItem(item)
        : (draft?.baseline ?? buildCreateFormData(initialValues)),
    [mode, item, draft, initialValues],
  );
  // Untouched fields render server truth, so background refreshes adopt
  // silently by construction; edited fields render the draft.
  const formData = useMemo(
    () => draftFormData(draft, serverForm),
    [draft, serverForm],
  );
  // Render-time mirror so the reconcile effect and handlers read the current
  // form without re-running per keystroke.
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  /**
   * Applies a form-shaped update by writing each changed field into the
   * draft. Callers spread the whole form — an absent key means "unchanged",
   * not "removed".
   */
  const setFormData = useCallback(
    (update: React.SetStateAction<ItemFormData>) => {
      if (!draftId) {
        return;
      }
      const prev = formDataRef.current;
      const next = typeof update === "function" ? update(prev) : update;
      for (const key of Object.keys(next) as (keyof ItemFormData)[]) {
        if (!isSameValue(next[key], prev[key])) {
          setDraftValue(draftId, key, next[key]);
        }
      }
    },
    [draftId, setDraftValue],
  );
  // Displayed price-model inputs: the draft's per-sub-key merge over server
  // truth. Edits write back into the draft like every other form field.
  const priceModelInputValues = useMemo(
    () => (formData.priceModelInputs ?? {}) as JSONObject,
    [formData.priceModelInputs],
  );

  /**
   * Replaces the price model inputs wholesale for a model switch, dropping
   * the provenance that belonged to the previous selection.
   */
  const resetPriceModelInputs = (values: JSONValue) => {
    if (draftId) {
      setDraftValue(
        draftId,
        "priceModelInputs",
        isJSONObject(values) ? values : {},
      );
    }
    setBaselinePriceModelInputs(values);
    setPriceModelInputMetadata([]);
    // Pin to the current persisted signature: the dropped provenance must
    // not be re-adopted until the server actually writes new metadata.
    syncedMetadataSignatureRef.current = persistedMetadataSignature;
  };
  // The org's allowed units replace the default list, keeping the item's
  // current unit listed so the select can still show it.
  const itemUnitOptions = allowedItemUnits
    ? ItemUnitSchema.options.filter(
        (unit) => allowedItemUnits.includes(unit) || unit === formData.unit,
      )
    : getItemUnitOptions(epicorPrimitiveSupportEnabled, formData.unit);
  const isPartNumberTooLong =
    maxPartNumberLength !== null && formData.name.length > maxPartNumberLength;
  const partNumberLengthNote = maxPartNumberLength !== null && (
    <p
      className={cn(
        "mt-1 text-right text-xs tabular-nums",
        isPartNumberTooLong ? "text-destructive" : "text-muted-foreground",
      )}
    >
      {formData.name.length}/{maxPartNumberLength} characters
    </p>
  );
  const conversionUnitOptions = getConversionUnitOptions(
    epicorPrimitiveSupportEnabled,
    [
      formData.unit,
      ...formData.unitConversions.flatMap((conversion) => [
        conversion.fromUnit,
        conversion.toUnit,
      ]),
    ],
  );
  // Instant-save fields display the overlay while saving, else the store item.
  const pendingAssetEntry = draftId
    ? pendingAssetsByItem.get(draftId)
    : undefined;
  const uploadedAssets = useMemo(
    () => pendingAssetEntry?.assets ?? item?.fileAssets ?? [],
    [pendingAssetEntry, item],
  );
  const primaryAssetId = pendingAssetEntry
    ? pendingAssetEntry.primaryId
    : (item?.primaryFileAssetId ?? null);
  const linkedVaultFiles =
    (draftId ? pendingVaultFilesByItem.get(draftId) : undefined) ??
    item?.vaultFiles ??
    [];

  const upsertView = useViewContextStore((state) => state.upsertView);
  const clearView = useViewContextStore((state) => state.clearView);
  const viewKey = useId();
  // The BOM panel renders only for an item saved with BOM costing.
  const isSavedAsBOM = item?.costingMethod === CostingMethodEnum.BOM;
  const showBOMTabs =
    mode === "edit" &&
    formData.costingMethod === CostingMethodEnum.BOM &&
    isSavedAsBOM;

  // Rings the field a History row points at, once its form tab is showing.
  const rightColumnRef = useRef<HTMLDivElement>(null);
  const [removedRowNote, setRemovedRowNote] = useState<string | null>(null);
  const handleSelectHistoryField = useCallback(
    (target: ItemFieldTarget) => {
      if (target.tab === "bom" && !showBOMTabs) {
        return;
      }
      setActiveTab(target.tab);
      setRemovedRowNote(target.note ?? null);
      const { anchor } = target;
      if (!anchor) {
        return;
      }
      requestAnimationFrame(() =>
        highlightField(rightColumnRef.current, anchor),
      );
    },
    [showBOMTabs],
  );

  // Reports what this editor is showing so chat messages can carry it as
  // view context; the tabs merge in their own fields.
  useEffect(() => {
    if (!estimationStationEnabled || mode !== "edit" || !open || !item?.id) {
      return;
    }
    // Only report the selected drawing while it is actually on screen; an
    // off-screen selection reads as noise next to the active tab's content.
    const selectedAsset =
      activeLeftTab === "drawings"
        ? uploadedAssets[selectedAssetIndex]
        : undefined;
    upsertView(viewKey, {
      screen: "itemEditor",
      itemId: item.id,
      itemName: formData.name || undefined,
      activeTab: VIEW_TAB_BY_LEFT_TAB[activeLeftTab],
      activePanel: showBOMTabs && activeTab === "bom" ? "bom" : "details",
      drawingAssetId: selectedAsset?.id,
      drawingFileName: selectedAsset?.fileName,
    });
  }, [
    estimationStationEnabled,
    mode,
    open,
    item?.id,
    formData.name,
    activeLeftTab,
    activeTab,
    showBOMTabs,
    uploadedAssets,
    selectedAssetIndex,
    upsertView,
    viewKey,
  ]);

  // Drop this editor's report on close or unmount; a closed one never reported.
  useEffect(() => {
    if (!open) {
      return;
    }
    return () => clearView(viewKey);
  }, [open, clearView, viewKey]);

  // The server owns provenance, so adopt whatever it last persisted for the
  // displayed item — on load, on tree selection, and after each save.
  const persistedMetadataSignature = JSON.stringify(
    item?.priceModelInputMetadata ?? [],
  );
  useEffect(() => {
    if (syncedMetadataSignatureRef.current === persistedMetadataSignature) {
      return;
    }
    syncedMetadataSignatureRef.current = persistedMetadataSignature;
    setPriceModelInputMetadata(item?.priceModelInputMetadata ?? []);
    setBaselinePriceModelInputs((item?.priceModelInputs ?? {}) as JSONValue);
  }, [persistedMetadataSignature, item]);

  /**
   * Opens (or revisits) the draft for a server item and seeds the view-only
   * editor state. A revisited dirty draft reconciles against the current
   * server copy: silent rebase, or the conflict banner on disagreement.
   */
  const openItemDraft = useCallback(
    (target: HydratedItem) => {
      const targetForm = getFormDataFromItem(target);
      ensureDraft(target.id, targetForm);
      openedDraftIdsRef.current.add(target.id);
      lastReconciledRef.current = {
        itemId: target.id,
        updatedAtMs: target.updatedAt.getTime(),
      };
      setConflictItem(null);
      // A revisited draft reconciles against the current server copy: banner
      // on disagreement, otherwise rebase — clean drafts included, so edits
      // are never measured against a baseline the server has moved past.
      // (A freshly ensured draft holds this very targetForm, so it skips.)
      const revisited = useItemDraftsStore
        .getState()
        .draftsByItemId.get(target.id);
      if (revisited && revisited.baseline !== targetForm) {
        if (draftConflictKeys(revisited, targetForm).length > 0) {
          setConflictItem(target);
        } else {
          rebaseDraft(target.id, targetForm);
        }
      }
      const loadedAssets = target.fileAssets || [];
      const primaryIndex = loadedAssets.findIndex(
        (a) => a.id === target.primaryFileAssetId,
      );
      setSelectedAssetIndex(primaryIndex >= 0 ? primaryIndex : 0);

      const hasMultipleCostBreaks = (target.costBreaks?.length || 0) > 1;
      setEnableCostBreaks(hasMultipleCostBreaks);

      const itemMargins =
        target.margin?.filter((m) => m.type === MarginTypeEnum.all) || [];
      setEnableItemMargins(
        itemMargins.length > 0 &&
          target.costingMethod === CostingMethodEnum.UNIT_COST,
      );

      // Cost-break/margin stashes are per-item conveniences; a different
      // item must not inherit them.
      unitCostStashRef.current = null;
      bomMarginsStashRef.current = null;

      setBaselinePriceModelInputs((target.priceModelInputs ?? {}) as JSONValue);
    },
    [ensureDraft, rebaseDraft],
  );

  // Fetch root item when sheet opens
  useEffect(() => {
    if (!open) {
      return;
    }
    fetchPriceModels({ includeArchived: true });
    if (mode === "edit" && itemId) {
      fetchItem(itemId, true);
    } else if (mode === "create") {
      clearDraft(CREATE_DRAFT_ID);
      ensureDraft(CREATE_DRAFT_ID, buildCreateFormData(initialValues));
      openedDraftIdsRef.current.add(CREATE_DRAFT_ID);
      writePendingAssets(initialPendingAssets(mode));
      setSelectedAssetIndex(0);
      writePendingVaultFiles(initialPendingVaultFiles(mode));

      setEnableCostBreaks(false);
      setEnableItemMargins(false);
      setSelectedPriceModelId(null);
      resetPriceModelInputs({});
      unitCostStashRef.current = null;
      bomMarginsStashRef.current = null;
    }
  }, [
    open,
    mode,
    itemId,
    initialValues,
    fetchItem,
    fetchPriceModels,
    clearDraft,
    ensureDraft,
    writePendingAssets,
    writePendingVaultFiles,
  ]);

  useEffect(() => {
    if (!open) {
      return;
    }
    fetchItemSettings({ force: true }).then(() => {
      if (useItemSettingsStore.getState().error) {
        toast.error("Failed to load item settings");
      }
    });
  }, [open, fetchItemSettings]);

  // Initialize form from root item on first load (only when not viewing a sub-component)
  const hasInitializedRef = React.useRef(false);
  useEffect(() => {
    hasInitializedRef.current = false;
  }, [itemId]);
  useEffect(() => {
    if (!open) {
      hasInitializedRef.current = false;
      return;
    }
    if (hasInitializedRef.current) {
      return;
    }
    if (mode === "edit" && storeItem && !isLoading) {
      hasInitializedRef.current = true;
      openItemDraft(storeItem);
    }
  }, [open, mode, storeItem, isLoading, openItemDraft]);

  // Initialize tree selection when assembly first loads
  useEffect(() => {
    if (open && rootItem && isAssembly) {
      setSelectedTreeItemId((prev) => prev ?? rootItem.id);
    }
    if (!open) {
      setSelectedTreeItemId(null);
    }
  }, [open, rootItem, isAssembly]);

  // When tree selection changes, apply the new item to the form
  const prevTreeItemIdRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (
      !selectedTreeItemId ||
      selectedTreeItemId === prevTreeItemIdRef.current
    ) {
      return;
    }
    prevTreeItemIdRef.current = selectedTreeItemId;
    // Open against the same copy the editor displays — the embedded tree
    // copy can be staler than the component's own store entry.
    const targetItem = selectedTreeComponent
      ? resolveDisplayItem(
          selectedTreeComponent,
          useItemsStore.getState().hydratedItems.get(selectedTreeItemId),
        )
      : rootItem;
    if (targetItem) {
      openItemDraft(targetItem);
    }
  }, [selectedTreeItemId, selectedTreeComponent, rootItem, openItemDraft]);

  const resetAndClose = () => {
    for (const id of openedDraftIdsRef.current) {
      clearDraft(id);
    }
    openedDraftIdsRef.current.clear();
    latestAssetSaveRef.current.clear();
    writePendingAssets(initialPendingAssets(mode));
    setSelectedAssetIndex(0);
    latestVaultSaveRef.current.clear();
    writePendingVaultFiles(initialPendingVaultFiles(mode));

    setEnableCostBreaks(false);
    setEnableItemMargins(false);
    setSelectedPriceModelId(null);
    resetPriceModelInputs({});
    unitCostStashRef.current = null;
    bomMarginsStashRef.current = null;
    lastReconciledRef.current = null;
    setConflictItem(null);
    setShowCloseConfirm(false);
    onOpenChange(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      onOpenChange(true);
      return;
    }
    const draftsByItemId = useItemDraftsStore.getState().draftsByItemId;
    const anyDraftDirty = [...openedDraftIdsRef.current].some(
      (id) => draftDirtyKeys(draftsByItemId.get(id)).length > 0,
    );
    if (isDirty || anyDraftDirty) {
      setShowCloseConfirm(true);
      return;
    }
    resetAndClose();
  };

  // Get the linked price model for the current item
  const linkedPriceModel: PriceModel | undefined = useMemo(() => {
    const pmId = formData.priceModelId;
    if (!pmId) {
      return undefined;
    }
    return priceModelsMap.get(pmId);
  }, [formData.priceModelId, priceModelsMap]);

  const hasPriceModel = Boolean(formData.priceModelId);

  const activePriceModel = useMemo(
    () =>
      linkedPriceModel ||
      (selectedPriceModelId
        ? priceModelsMap.get(selectedPriceModelId)
        : undefined),
    [linkedPriceModel, selectedPriceModelId, priceModelsMap],
  );

  const priceModelInputSchema = useMemo(() => {
    if (!activePriceModel?.inputSchema) {
      return null;
    }
    return activePriceModel.inputSchema as JSONSchema7;
  }, [activePriceModel]);

  // Computed (fpCalculation) fields render inline (read-only) alongside the
  // inputs, in their natural schema position — not a separate section. We keep
  // the full schema and just track which keys are computed so we can merge
  // their values in for display and strip them back out of the editable inputs.
  const { calculationFields } = useMemo(
    () => splitCalculationFields(priceModelInputSchema),
    [priceModelInputSchema],
  );
  const calculationKeys = useMemo(
    () => new Set(calculationFields.map((f) => f.key)),
    [calculationFields],
  );

  const calculationValues =
    item?.priceModelId === formData.priceModelId
      ? Object.fromEntries(
          Object.entries(item?.priceModelCalculations ?? {}).filter(([key]) =>
            calculationKeys.has(key),
          ),
        )
      : {};

  // Input sub-keys changed since the last save, from the draft's record
  // edits. Drives the "pending edit" dot in the form gutter; clears when the
  // draft rebases on save. Edit mode only — create mode has no saved baseline.
  const modifiedFields = useMemo(() => {
    if (mode !== "edit" || !item) {
      return new Set<string>();
    }
    const outputs: Record<string, unknown> = item.priceModelCalculations ?? {};
    // Skip computed outputs (current schema calc fields + any leftover calc
    // keys) so only real input edits count.
    return new Set(
      Object.keys(recordFieldEdits(draft, "priceModelInputs")).filter(
        (key) => !calculationKeys.has(key) && !(key in outputs),
      ),
    );
  }, [mode, item, draft, calculationKeys]);

  const dirtySet = useMemo(() => new Set(draftDirtyKeys(draft)), [draft]);
  const isDirty = dirtySet.size > 0 || modifiedFields.size > 0;

  // Reconcile background refreshes: untouched fields adopt automatically
  // through the draft overlay; the banner appears only when the server
  // disagrees with an unsaved edit. Own save echoes rebase silently.
  useEffect(() => {
    if (!open || mode !== "edit" || !item || !draft) {
      return;
    }
    const last = lastReconciledRef.current;
    const versionMs = item.updatedAt.getTime();
    // Only strictly newer versions reconcile — equal is our own echo, older
    // is a late out-of-order response that must not roll the form back.
    if (last && last.itemId === item.id && versionMs <= last.updatedAtMs) {
      return;
    }
    lastReconciledRef.current = { itemId: item.id, updatedAtMs: versionMs };
    if (!last || last.itemId !== item.id) {
      return;
    }
    const theirForm = getFormDataFromItem(item);
    if (draftConflictKeys(draft, theirForm).length > 0) {
      setConflictItem(item);
      return;
    }
    const theirInputs: Record<string, unknown> =
      theirForm.priceModelInputs ?? {};
    const baseInputs: Record<string, unknown> =
      draft.baseline.priceModelInputs ?? {};
    const inputKeys = new Set([
      ...Object.keys(theirInputs),
      ...Object.keys(baseInputs),
    ]);
    // Against the baseline, not the displayed form: untouched fields already
    // render server truth, so comparing to the form would only report our own
    // dirty keys instead of what actually moved.
    const isForeignChange =
      (Object.keys(theirForm) as (keyof ItemFormData)[]).some(
        (key) =>
          // Inputs are compared per sub-key below, ignoring computed keys.
          key !== "priceModelInputs" &&
          !isSameValue(theirForm[key], draft.baseline[key]),
      ) ||
      [...inputKeys].some(
        (key) =>
          !calculationKeys.has(key) &&
          !isSameValue(theirInputs[key], baseInputs[key]),
      );
    setConflictItem(null);
    rebaseDraft(item.id, theirForm);
    // Sparkles re-baseline to the adopted server inputs; the user's remaining
    // edits keep counting against the fresh copy.
    setBaselinePriceModelInputs((item.priceModelInputs ?? {}) as JSONValue);
    if (isForeignChange) {
      toast.info("Item updated with the latest changes");
    }
  }, [item, open, mode, draft, calculationKeys, rebaseDraft]);

  // Form data shown to the inline price-model form: editable inputs plus the
  // computed (fpCalculation) values merged in for their read-only display.
  const priceModelFormData: JSONObject = {
    ...(isJSONObject(priceModelInputValues) ? priceModelInputValues : {}),
    ...calculationValues,
  };

  // Dragging a row moves its values to a new position without changing any of
  // them, so the baseline's rows are realigned first. Comparing positions after
  // that reports a reorder as no edit at all, exactly as the server will.
  const alignedBaselineInputs = useMemo(
    () => alignBaselineRows(baselinePriceModelInputs, priceModelInputValues),
    [baselinePriceModelInputs, priceModelInputValues],
  );

  // Every input leaf that currently differs from the baseline. Derived rather
  // than accumulated, so a value typed back to what it was stops counting as
  // an edit, matching what the server concludes on save.
  const locallyEditedPaths = useMemo(
    () =>
      new Set(
        collectChangedLeafPaths(alignedBaselineInputs, priceModelInputValues)
          .filter((path) => !calculationKeys.has(path[0]))
          .map((path) => metadataPathKey(path)),
      ),
    [alignedBaselineInputs, priceModelInputValues, calculationKeys],
  );

  // Writing the baseline value back drops the path out of `locallyEditedPaths`
  // on the next render, which is what brings the field's sparkle back.
  const revertPriceModelInputPath = useCallback(
    (schemaPath: string[]) => {
      if (!draftId) {
        return;
      }
      const reverted = setValueAtPath(
        priceModelInputValues,
        schemaPath,
        readValueAtPath(alignedBaselineInputs, schemaPath),
      );
      setDraftValue(
        draftId,
        "priceModelInputs",
        isJSONObject(reverted) ? reverted : {},
      );
    },
    [draftId, priceModelInputValues, alignedBaselineInputs, setDraftValue],
  );

  const priceModelInputFormContext: PriceModelInputFormContext = useMemo(() => {
    const defaults = activePriceModel
      ? getEffectiveDefaults(activePriceModel)
      : {};
    return {
      metadataByPath: indexMetadataByPath(
        priceModelInputMetadata,
        baselinePriceModelInputs,
        priceModelInputValues,
      ),
      locallyEditedPaths,
      priceModelName: activePriceModel?.name ?? "this price model",
      effectiveDefaults: isJSONObject(defaults) ? defaults : {},
      baselineInputs: alignedBaselineInputs,
      revertPath: revertPriceModelInputPath,
    };
  }, [
    priceModelInputMetadata,
    locallyEditedPaths,
    activePriceModel,
    alignedBaselineInputs,
    baselinePriceModelInputs,
    priceModelInputValues,
    revertPriceModelInputPath,
  ]);

  // On change, drop the computed fields back out — they're outputs, not inputs,
  // so they never get persisted or sent to the model as inputs.
  const handlePriceModelInputsChange = (data: JSONValue) => {
    if (!draftId) {
      return;
    }
    const next: JSONObject = {
      ...(isJSONObject(data) ? data : {}),
    };
    for (const key of calculationKeys) {
      delete next[key];
    }
    setDraftValue(draftId, "priceModelInputs", next);
  };

  const handleFileUpload = async (file: File) => {
    if (isReadOnly) {
      return;
    }
    setIsUploadingFile(true);
    try {
      // Get presigned URL
      const uploadResponse = await fetchApi("api/assets/upload", "POST", {
        body: {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        },
      });

      if (!uploadResponse.ok || !uploadResponse.data) {
        throw new Error("Failed to get upload URL");
      }

      const { uploadUrl, asset } = uploadResponse.data as {
        uploadUrl: string;
        asset: Asset;
      };

      // Upload file to presigned URL
      await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      // Computed from the ref so concurrent multi-file uploads accumulate
      // instead of last-write-winning against each other.
      const current = currentAssetState();
      const nextAssets = [...current.assets, asset];
      const nextPrimary =
        current.assets.length === 0 ? asset.id : current.primaryId;
      const saved = await saveAssetState({
        assets: nextAssets,
        primaryId: nextPrimary,
      });
      if (saved) {
        toast.success("File uploaded successfully");
      }
    } catch (_error) {
      toast.error("Failed to upload file");
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach(handleFileUpload);
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files) {
      Array.from(files).forEach(handleFileUpload);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleRemoveAsset = async (assetId: string) => {
    if (isReadOnly) {
      return;
    }
    const current = currentAssetState();
    const assetIndex = current.assets.findIndex((a) => a.id === assetId);
    const newAssets = current.assets.filter((a) => a.id !== assetId);

    // Adjust selected index if needed
    if (assetIndex === selectedAssetIndex && newAssets.length > 0) {
      setSelectedAssetIndex(Math.min(selectedAssetIndex, newAssets.length - 1));
    } else if (newAssets.length === 0) {
      setSelectedAssetIndex(0);
    }

    // If this was the primary asset, clear it or set a new one
    const nextPrimary =
      current.primaryId === assetId
        ? (newAssets[0]?.id ?? null)
        : current.primaryId;
    const saved = await saveAssetState({
      assets: newAssets,
      primaryId: nextPrimary,
    });
    if (saved && mode === "edit") {
      toast.success("File removed successfully");
    }
  };

  const handleSetPrimaryAsset = async (assetId: string) => {
    if (isReadOnly) {
      return;
    }
    const current = currentAssetState();
    const saved = await saveAssetState({
      assets: current.assets,
      primaryId: assetId,
    });
    if (saved && mode === "edit") {
      toast.success("Primary asset updated");
    }
  };

  /** The freshest asset set for the displayed item: its in-flight overlay, else the store item. */
  const currentAssetState = (): PendingAssetState =>
    (draftId ? pendingAssetsRef.current.get(draftId) : undefined) ?? {
      assets: item?.fileAssets ?? [],
      primaryId: item?.primaryFileAssetId ?? null,
    };

  /**
   * Persists an asset-set change for the item displayed at call time. Saves
   * chain through a queue and clear only their own item's overlay, mirroring
   * the vault-link mechanism; create mode only records the overlay.
   */
  const saveAssetState = async (next: PendingAssetState): Promise<boolean> => {
    if (!draftId) {
      return false;
    }
    const targetId = draftId;
    setPendingAssetsFor(targetId, next);
    if (mode !== "edit" || !item) {
      return true;
    }
    const save = assetSaveQueueRef.current
      .then(() => persistAssetState(targetId, next))
      .catch(() => {
        toast.error("Failed to update item");
        return false;
      })
      .finally(() => {
        if (latestAssetSaveRef.current.get(targetId) === save) {
          latestAssetSaveRef.current.delete(targetId);
          setPendingAssetsFor(targetId, null);
        }
      });
    latestAssetSaveRef.current.set(targetId, save);
    assetSaveQueueRef.current = save;
    return await save;
  };

  /** Writes one asset set — a tree component saves through the store, the root through the sheet's onSave. */
  const persistAssetState = async (
    targetId: string,
    next: PendingAssetState,
  ): Promise<boolean> => {
    if (mode !== "edit") {
      return false;
    }
    const payload = {
      fileAssetIds: next.assets.map((asset) => asset.id),
      primaryFileAssetId: next.primaryId,
    };
    if (targetId !== itemId) {
      const { error } = await updateItem(targetId, payload);
      if (error) {
        toast.error(error);
        return false;
      }
      await fetchItem(itemId, true);
      return true;
    }
    const result = await onSave(payload);
    if (result !== true) {
      toast.error(result);
      return false;
    }
    return true;
  };

  const handlePinVaultFile = async (file: HydratedOrganizationFile) => {
    if (currentVaultFiles().some((linked) => linked.id === file.id)) {
      return;
    }
    await saveVaultFileLinks([...currentVaultFiles(), file]);
  };

  const handleUnpinVaultFile = async (fileId: string) => {
    const remaining = currentVaultFiles().filter((file) => file.id !== fileId);
    if (remaining.length === currentVaultFiles().length) {
      return;
    }
    await saveVaultFileLinks(remaining);
  };

  /** The freshest vault links for the displayed item: its in-flight overlay, else the store item. */
  const currentVaultFiles = () =>
    (draftId ? pendingVaultFilesRef.current.get(draftId) : undefined) ??
    item?.vaultFiles ??
    [];

  /**
   * Persists the item's vault links. Edit mode saves immediately, matching how
   * asset changes behave; create mode carries them until the item is created.
   * When a tree component is displayed, the links belong to it, so they save
   * through the store — the sheet-level onSave is bound to the root item.
   */
  const saveVaultFileLinks = async (files: HydratedOrganizationFile[]) => {
    if (!draftId) {
      return;
    }
    // Captured now: the save must land on the item that was displayed at click
    // time, even if the user switches tree selection while it is queued.
    const targetId = draftId;
    setPendingVaultFilesFor(targetId, files);
    const organizationFileIds = files.map((file) => file.id);

    if (mode !== "edit" || !item) {
      // Create mode carries the links in the draft until the item is created.
      setFormData((prev) => ({ ...prev, organizationFileIds }));
      return;
    }
    const save = vaultLinkSaveQueueRef.current
      .then(() => persistVaultFileLinks(targetId, organizationFileIds))
      .catch(() => {
        toast.error("Failed to update item");
      })
      .finally(() => {
        // This item's last queued save drops its overlay back to server truth,
        // which also reverts the optimistic state after a failure.
        if (latestVaultSaveRef.current.get(targetId) === save) {
          latestVaultSaveRef.current.delete(targetId);
          setPendingVaultFilesFor(targetId, null);
        }
      });
    latestVaultSaveRef.current.set(targetId, save);
    vaultLinkSaveQueueRef.current = save;
    await save;
  };

  /** Writes one vault-link set — a tree component saves through the store, the root through the sheet's onSave. */
  const persistVaultFileLinks = async (
    targetId: string,
    organizationFileIds: string[],
  ) => {
    if (mode !== "edit") {
      return;
    }
    if (targetId !== itemId) {
      const { error } = await updateItem(targetId, { organizationFileIds });
      if (error) {
        toast.error(error);
        return;
      }
      await fetchItem(itemId, true);
      return;
    }
    const result = await onSave({ organizationFileIds });
    if (result !== true) {
      toast.error(result);
    }
  };

  const navigateAsset = (direction: "prev" | "next") => {
    if (uploadedAssets.length === 0) {
      return;
    }

    if (direction === "next") {
      setSelectedAssetIndex((prev) => (prev + 1) % uploadedAssets.length);
    } else {
      setSelectedAssetIndex((prev) =>
        prev === 0 ? uploadedAssets.length - 1 : prev - 1,
      );
    }
  };

  const getItemMargins = (): ItemMargin[] => {
    return formData.margin?.filter((m) => m.type === MarginTypeEnum.all) || [];
  };

  const handleItemMarginsChange = (updatedItemMargins: ItemMargin[]) => {
    const otherMargins =
      formData.margin?.filter((m) => m.type !== MarginTypeEnum.all) || [];
    setFormData({
      ...formData,
      margin: [...otherMargins, ...updatedItemMargins],
    });
  };

  const handleSave = async () => {
    if (formData.margin && formData.margin.length > 0) {
      for (const margin of formData.margin) {
        if (margin.marginAmount.type === "PERCENTAGE") {
          const percentage = new Decimal(margin.marginAmount.percentage || "0");
          if (percentage.greaterThanOrEqualTo(100)) {
            toast.error(
              `Margin percentage must be less than 100%. Got: ${percentage.toString()}%`,
            );
            return;
          }
        }
      }
    }

    if (isReadOnly) {
      if (!item) {
        return;
      }
      // BOMMarginEditor persists BOM margins directly via handleUpdateItem.
      if (formData.costingMethod === CostingMethodEnum.BOM) {
        return;
      }
      setIsSaving(true);
      try {
        const { item: updatedItem, error } = await updateItem(item.id, {
          margin: formData.margin || [],
        });
        if (!updatedItem) {
          toast.error(error ?? "Failed to update item margins");
          return;
        }
        markOwnSavedVersion(item.id);
        if (itemId) {
          await fetchItem(itemId, true);
        }
        onBomUpdated?.();
        toast.success("Item margins updated successfully");
      } finally {
        setIsSaving(false);
      }
      return;
    }

    if (!formData.name.trim()) {
      toast.error("Item name is required");
      return;
    }

    const costBreaks = formData.costBreaks.filter(
      (costBreak) => costBreak.unitCost.trim() !== "",
    );
    if (
      !selectedPriceModelId &&
      (formData.costingMethod === CostingMethodEnum.UNIT_COST ||
        costBreaks.length > 0)
    ) {
      if (costBreaks.length === 0) {
        toast.error("At least one cost break is required");
        return;
      }

      const invalidQtyBreak = costBreaks.find((cb) => cb.minQty <= 0);
      if (invalidQtyBreak) {
        toast.error(
          "All quantity breaks must have a minimum quantity greater than 0",
        );
        return;
      }

      const hasMinQtyOne = costBreaks.some((cb) => cb.minQty === 1);
      if (!hasMinQtyOne) {
        toast.error("A cost break with minimum quantity of 1 is required");
        return;
      }
    }

    setIsSaving(true);
    try {
      if (mode === "create") {
        const priceModelInputs =
          selectedPriceModelId && isJSONObject(priceModelInputValues)
            ? (priceModelInputValues as Record<string, unknown>)
            : undefined;

        const saveData: CreateItemParams = {
          name: formData.name.trim(),
          revision: formData.revision.trim() || "",
          type: selectedPriceModelId
            ? ItemTypeEnum.MANUFACTURED
            : formData.type,
          costingMethod: selectedPriceModelId
            ? CostingMethodEnum.BOM
            : formData.costingMethod,
          unit: formData.unit,
          weight: formData.weight,
          weightUnit: formData.weightUnit,
          dimensionUnit: formData.dimensionUnit,
          length: formData.length,
          width: formData.width,
          height: formData.height,
          allowance: formData.allowance,
          unitConversions: formData.unitConversions,
          costBreaks: selectedPriceModelId
            ? undefined
            : costBreaks.length > 0
              ? costBreaks
              : undefined,
          description: formData.description?.trim() || undefined,
          briefing: formData.briefing.trim() || undefined,
          organizationFileIds: formData.organizationFileIds,
          primaryFileAssetId: primaryAssetId || undefined,
          fileAssetIds: uploadedAssets.map((a) => a.id),
          margin: formData.margin || [],
          priceModelId: selectedPriceModelId || undefined,
          priceModelInputs,
          customFields: formData.customFields,
        };
        const createResult = await (
          onSave as (data: CreateItemParams) => Promise<true | string>
        )(saveData);
        if (createResult === true) {
          clearDraft(CREATE_DRAFT_ID);
          openedDraftIdsRef.current.delete(CREATE_DRAFT_ID);
          toast.success("Item created successfully");
          handleOpenChange(false);
        } else {
          toast.error(createResult);
        }
      } else {
        const effectivePriceModelId =
          formData.costingMethod === CostingMethodEnum.UNIT_COST
            ? null
            : (formData.priceModelId ?? null);
        const parsedPriceModelInputs =
          effectivePriceModelId && isJSONObject(priceModelInputValues)
            ? priceModelInputValues
            : undefined;
        // The costing/pricing fields are sent as a group when any of them
        // changed, so the server recomputes against a coherent set.
        const priceModelGroupDirty =
          dirtySet.has("costingMethod") ||
          dirtySet.has("priceModelId") ||
          dirtySet.has("priceModelInputs");
        // Only the dirty fields are sent, so a stale form value can never
        // overwrite a concurrent agent edit to an untouched field. The
        // instant-save asset/vault fields persist through their own onSave
        // calls and are deliberately never part of this payload.
        const saveData: UpdateItemParams = {
          ...(dirtySet.has("name") && { name: formData.name.trim() }),
          ...(dirtySet.has("revision") && {
            revision: formData.revision.trim(),
          }),
          ...(dirtySet.has("type") && { type: formData.type }),
          ...(priceModelGroupDirty && {
            costingMethod: formData.costingMethod,
          }),
          ...(dirtySet.has("unit") && { unit: formData.unit }),
          ...(dirtySet.has("weight") && { weight: formData.weight }),
          ...(dirtySet.has("weightUnit") && {
            weightUnit: formData.weightUnit,
          }),
          ...(dirtySet.has("dimensionUnit") && {
            dimensionUnit: formData.dimensionUnit,
          }),
          ...(dirtySet.has("length") && { length: formData.length }),
          ...(dirtySet.has("width") && { width: formData.width }),
          ...(dirtySet.has("height") && { height: formData.height }),
          ...(dirtySet.has("allowance") && {
            allowance: formData.allowance,
          }),
          ...(dirtySet.has("unitConversions") && {
            unitConversions: formData.unitConversions,
          }),
          // Sent as an array even when empty: undefined would read as "no
          // change" server-side and silently keep the removed breaks.
          ...((dirtySet.has("costBreaks") || priceModelGroupDirty) && {
            costBreaks,
          }),
          ...(dirtySet.has("description") && {
            // Empty saves as null so a description can be cleared
            description: formData.description?.trim() || null,
          }),
          ...(dirtySet.has("briefing") && {
            // Empty saves as null so a briefing can be cleared (undefined would preserve it)
            briefing: formData.briefing.trim() || null,
          }),
          // BOM margins are managed by the BOMMarginEditor — omit here to avoid overwriting them
          ...(dirtySet.has("margin") &&
            formData.costingMethod !== CostingMethodEnum.BOM && {
              margin: formData.margin || [],
            }),
          ...(priceModelGroupDirty && {
            priceModelId: effectivePriceModelId,
            priceModelInputs: parsedPriceModelInputs,
          }),
          ...(dirtySet.has("customFields") && {
            customFields: formData.customFields,
          }),
        };
        // When viewing a tree component, save it directly via the store
        if (selectedTreeComponent && selectedTreeItemId !== itemId) {
          const { item: updatedComponent, error } = await updateItem(
            selectedTreeItemId!,
            saveData,
          );
          if (updatedComponent) {
            markOwnSavedVersion(selectedTreeItemId!);
            await fetchItem(itemId, true);
            reseedDraftFromStore(selectedTreeItemId!);
            onBomUpdated?.();
            toast.success("Component updated successfully");
          } else {
            toast.error(error ?? "Failed to update component");
          }
        } else {
          const editResult = await (
            onSave as (data: UpdateItemParams) => Promise<true | string>
          )(saveData);
          if (editResult === true) {
            markOwnSavedVersion(itemId);
            await fetchItem(itemId, true);
            reseedDraftFromStore(itemId);
            onBomUpdated?.();
            toast.success("Item updated successfully");
          } else {
            toast.error(editResult);
          }
        }
      }
    } catch {
      toast.error(
        mode === "create" ? "Failed to create item" : "Failed to update item",
      );
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Pins the reconcile watermark to the version our own save produced, so its
   * echo is skipped deterministically rather than by winning a render race.
   */
  const markOwnSavedVersion = (savedItemId: string) => {
    const saved = useItemsStore.getState().items.get(savedItemId);
    if (saved) {
      lastReconciledRef.current = {
        itemId: savedItemId,
        updatedAtMs: saved.updatedAt.getTime(),
      };
    }
  };

  /**
   * Rebases the saved item's draft onto the refetched server copy: saved keys
   * prune away, keys typed while the save was in flight survive.
   */
  const reseedDraftFromStore = (savedItemId: string) => {
    const refreshed = useItemsStore.getState().hydratedItems.get(savedItemId);
    if (!refreshed) {
      return;
    }
    rebaseDraft(savedItemId, getFormDataFromItem(refreshed));
    setBaselinePriceModelInputs(
      (refreshed.priceModelInputs ?? {}) as JSONValue,
    );
    lastReconciledRef.current = {
      itemId: savedItemId,
      updatedAtMs: refreshed.updatedAt.getTime(),
    };
    setConflictItem(null);
  };

  /** The freshest copy of the conflicted item — the banner's snapshot may be stale. */
  const resolveConflictTarget = () =>
    conflictItem
      ? (useItemsStore.getState().hydratedItems.get(conflictItem.id) ??
        conflictItem)
      : null;

  /** Re-applies the refreshed item, discarding the user's unsaved edits. */
  const handleLoadTheirChanges = () => {
    const target = resolveConflictTarget();
    if (!target) {
      return;
    }
    clearDraft(target.id);
    openItemDraft(target);
  };

  /** Keeps the user's edits and adopts the server's values for everything else. */
  const handleKeepMyChanges = () => {
    const target = resolveConflictTarget();
    if (!target) {
      return;
    }
    rebaseDraft(target.id, getFormDataFromItem(target));
    setBaselinePriceModelInputs((target.priceModelInputs ?? {}) as JSONValue);
    lastReconciledRef.current = {
      itemId: target.id,
      updatedAtMs: target.updatedAt.getTime(),
    };
    setConflictItem(null);
  };

  // Build breadcrumb path from root to selected item
  const breadcrumbPath = useMemo(() => {
    if (!isAssembly || !rootItem || !selectedTreeItemId) {
      return [];
    }
    return buildBreadcrumbPath(rootItem, selectedTreeItemId);
  }, [isAssembly, rootItem, selectedTreeItemId]);

  // Header content
  const header = (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      {onToggleChat && (
        <Button
          variant="outline"
          size="sm"
          onClick={onToggleChat}
          className="shrink-0 gap-2"
        >
          <MessageSquare className="h-4 w-4" />
          {isChatCollapsed ? "Show Chat" : "Hide Chat"}
        </Button>
      )}
      <div
        className={cn(
          "flex min-w-0 flex-1 items-center gap-2 sm:gap-4",
          onToggleChat && "mx-auto",
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
          {item?.reviewStatus && (
            <ReviewStatusIndicator
              status={item.reviewStatus}
              showReviewed
              onClick={
                item.reviewStatus === "NEEDS_REVIEW" && !isReadOnly
                  ? () => markItemsReviewed([item.id])
                  : undefined
              }
            />
          )}
          {isViewingComponent && breadcrumbPath.length > 0 ? (
            <div className="flex min-w-0 items-center gap-1 overflow-hidden">
              {breadcrumbPath.map((crumb, i) => {
                const isLast = i === breadcrumbPath.length - 1;
                return (
                  <span
                    key={crumb.id}
                    className="flex min-w-0 items-center gap-1"
                  >
                    {i > 0 && (
                      <span className="text-muted-foreground text-sm">/</span>
                    )}
                    {isLast ? (
                      <span className="truncate text-xl font-semibold">
                        {crumb.name}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setSelectedTreeItemId(crumb.id)}
                        className="truncate text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {crumb.name}
                      </button>
                    )}
                  </span>
                );
              })}
            </div>
          ) : (
            <h2 className="truncate text-xl font-semibold">
              {mode === "create"
                ? "Create New Item"
                : isLoading
                  ? "Loading Item..."
                  : `Edit Item: ${item?.name || ""}`}
            </h2>
          )}
        </div>
        {item?.revision && item.revision !== "" && (
          <Badge variant="secondary" className="shrink-0">
            Rev {item.revision}
          </Badge>
        )}
        {isSynced && (
          <Badge variant="outline" className="hidden shrink-0 sm:inline-flex">
            Synced
          </Badge>
        )}
        {isAgentWorking && <AgentWorkingBadge />}
        {linkedPriceModel && (
          <Badge
            variant="outline"
            className="hidden max-w-[15rem] shrink-0 truncate sm:inline-flex"
          >
            Price Model: {linkedPriceModel.name}
          </Badge>
        )}
      </div>
    </div>
  );

  const errorColumn = (
    <div className="flex h-full w-full items-center justify-center p-6">
      <div className="text-center text-destructive">
        <p>{fetchError}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => onOpenChange(false)}
        >
          Close
        </Button>
      </div>
    </div>
  );

  const leftColumn = fetchError ? (
    errorColumn
  ) : (
    <div className="h-full w-full flex flex-col p-4">
      <Tabs
        value={activeLeftTab}
        onValueChange={setActiveLeftTab}
        className="flex-1 flex flex-col min-h-0"
      >
        <TabsList variant="browser" className="min-w-0">
          <TabsTrigger
            variant="browser"
            className="min-w-12 gap-1 px-1.5"
            value="drawings"
            aria-label="Drawings"
            title="Drawings"
          >
            <DraftingCompass className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">Drawings</span>
          </TabsTrigger>
          <TabsTrigger
            variant="browser"
            className="min-w-12 gap-1 px-1.5"
            value="briefing"
            aria-label="Briefing"
            title="Briefing"
          >
            <NotebookPen className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">Briefing</span>
          </TabsTrigger>
          {estimationStationEnabled && (
            <TabsTrigger
              variant="browser"
              className="min-w-12 gap-1 px-1.5"
              value="vault"
              aria-label="Vault"
              title="Vault"
            >
              <Vault className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">Vault</span>
            </TabsTrigger>
          )}
          {isCommsMessagesEnabled ? (
            <TabsTrigger
              variant="browser"
              className="min-w-12 gap-1 px-1.5"
              value="comms"
              aria-label="Correspondence"
              title="Correspondence"
            >
              <MessageSquare className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">Correspondence</span>
            </TabsTrigger>
          ) : null}
          {hasInventory && item ? (
            <TabsTrigger
              variant="browser"
              className="min-w-12 gap-1 px-1.5"
              value="inventory"
              aria-label="Inventory"
              title="Inventory"
            >
              <Package className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">Inventory</span>
            </TabsTrigger>
          ) : null}
          {isItemHistoryEnabled ? (
            <TabsTrigger
              variant="browser"
              className="min-w-12 gap-1 px-1.5"
              value="history"
              aria-label="History"
              title="History"
            >
              <History className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">History</span>
            </TabsTrigger>
          ) : null}
        </TabsList>
        <TabsContent
          variant="browser"
          value="drawings"
          className="flex-1 flex flex-col min-h-0 overflow-hidden"
        >
          <ItemDrawingsTab
            uploadedAssets={uploadedAssets}
            selectedAssetIndex={selectedAssetIndex}
            onSelectAsset={setSelectedAssetIndex}
            primaryAssetId={primaryAssetId}
            isUploadingFile={isUploadingFile}
            onNavigate={navigateAsset}
            onSetPrimaryAsset={handleSetPrimaryAsset}
            onRemoveAsset={handleRemoveAsset}
            onFileChange={handleFileChange}
            onFileDrop={handleFileDrop}
            onDragOver={handleDragOver}
          />
        </TabsContent>
        <TabsContent
          variant="browser"
          value="briefing"
          className="flex-1 min-h-0 overflow-hidden"
        >
          <ItemBriefingTab
            briefing={formData.briefing}
            onBriefingChange={(briefing) =>
              setFormData({ ...formData, briefing })
            }
            itemId={itemId}
          />
        </TabsContent>
        {estimationStationEnabled && (
          <TabsContent
            variant="browser"
            value="vault"
            className="flex-1 min-h-0 overflow-hidden"
          >
            <ItemVaultTab
              linkedFiles={linkedVaultFiles}
              onPin={handlePinVaultFile}
              onUnpin={handleUnpinVaultFile}
            />
          </TabsContent>
        )}
        {isCommsMessagesEnabled ? (
          <TabsContent
            variant="browser"
            value="comms"
            className="flex-1 min-h-0 overflow-hidden"
          >
            {itemId ? <ItemCommsMessagesTab itemId={itemId} /> : null}
          </TabsContent>
        ) : null}
        {hasInventory && item ? (
          <TabsContent
            variant="browser"
            value="inventory"
            className="flex-1 min-h-0 overflow-hidden"
          >
            <InventoryController
              key={`${item.organizationId}:${item.id}`}
              itemId={item.id}
            />
          </TabsContent>
        ) : null}
        {isItemHistoryEnabled ? (
          <TabsContent
            variant="browser"
            value="history"
            className="flex-1 min-h-0 overflow-hidden"
          >
            {item ? (
              <ItemHistoryTab
                item={item}
                onSelectField={handleSelectHistoryField}
              />
            ) : null}
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
  const quantities = useMemo(
    () =>
      propQuantities ??
      (item?.quotedQuantities?.length
        ? item.quotedQuantities
        : ["1", "10", "100", "1000"]),
    [item?.quotedQuantities, propQuantities],
  );
  // Shared price-model cost fetch, feeding both the floating recalc bar (during
  // input entry) and the static summary (above the detailed output).
  const { costs: priceModelCosts, loading: priceModelCostsLoading } =
    usePriceModelCosts(formData.priceModelId ? item?.id : null, quantities);
  const itemCustomFieldsSection =
    customFieldsSchema &&
    Object.keys(customFieldsSchema.properties).length > 0 ? (
      <div className="space-y-2">
        <h4 className="border-b border-border/80 pb-1.5 text-xs font-bold uppercase tracking-wider text-foreground/70">
          Custom Fields
        </h4>
        <CustomFieldsSection
          schema={customFieldsSchema}
          values={formData.customFields}
          readOnly={isReadOnly}
          layout="rows"
          onChange={(fieldName, value) =>
            setFormData({
              ...formData,
              customFields: {
                ...formData.customFields,
                [fieldName]: value,
              },
            })
          }
        />
      </div>
    ) : null;
  const itemUnitConversionsSection = (
    <ItemUnitConversionsEditor
      disabled={isReadOnly}
      value={formData.unitConversions}
      onChange={(unitConversions) =>
        setFormData({ ...formData, unitConversions })
      }
      unitOptions={conversionUnitOptions}
      itemUnit={formData.unit}
    />
  );

  // Right column - Form content
  const rightColumnContent = () => {
    const showBOMSaveHint =
      formData.costingMethod === CostingMethodEnum.BOM && !isSavedAsBOM;
    if (showBOMTabs) {
      return (
        <>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="bom">BOM & Pricing</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="mt-4">
              <fieldset disabled={isReadOnly} className="min-w-0 border-0 p-0">
                <div className="space-y-4 py-2">
                  <div className="divide-y">
                    <FormRow
                      anchor="name"
                      label="Name / Item Number"
                      htmlFor="name"
                      required
                      wide
                      note={partNumberLengthNote}
                    >
                      <Input
                        id="name"
                        value={formData.name}
                        maxLength={maxPartNumberLength ?? undefined}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                      />
                    </FormRow>
                    <FormRow
                      anchor="revision"
                      label="Revision"
                      htmlFor="revision"
                    >
                      <Input
                        id="revision"
                        value={formData.revision}
                        onChange={(e) =>
                          setFormData({ ...formData, revision: e.target.value })
                        }
                      />
                    </FormRow>
                    <div
                      className="py-2.5 space-y-1.5"
                      data-field-anchor="description"
                    >
                      <Label
                        htmlFor="description-bom"
                        className="text-sm font-medium leading-tight"
                      >
                        Description
                      </Label>
                      <Textarea
                        id="description-bom"
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        className="min-h-[80px]"
                      />
                    </div>
                    <FormRow anchor="type" label="Type" htmlFor="type" required>
                      <Select
                        disabled={isReadOnly}
                        value={formData.type}
                        onValueChange={(value: typeof formData.type) => {
                          const hasExistingContent =
                            formData.costBreaks.length > 0 ||
                            (item?.components?.length ?? 0) > 0 ||
                            (item?.operations?.length ?? 0) > 0;
                          const newCostingMethod = hasExistingContent
                            ? formData.costingMethod
                            : getDefaultCostingMethod(value);
                          setFormData({
                            ...formData,
                            type: value,
                            costingMethod: newCostingMethod,
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ItemTypeEnum.MANUFACTURED}>
                            Manufactured
                          </SelectItem>
                          <SelectItem value={ItemTypeEnum.PURCHASED}>
                            Purchased
                          </SelectItem>
                          <SelectItem value={ItemTypeEnum.MATERIAL}>
                            Material
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormRow>
                    <FormRow
                      anchor="costingMethod"
                      label="Costing Method"
                      note={
                        showBOMSaveHint ? (
                          <div className="mt-1.5">
                            <p className="flex items-center gap-1.5 text-xs text-muted-foreground animate-in fade-in slide-in-from-top-1 duration-200">
                              <Info className="h-3 w-3 shrink-0" />
                              Save item to view BOM & Pricing
                            </p>
                          </div>
                        ) : undefined
                      }
                    >
                      <div className="flex gap-1 p-1 bg-muted rounded-md">
                        <button
                          type="button"
                          className={cn(
                            "flex-1 px-3 py-1.5 text-sm font-medium rounded transition-all duration-200",
                            formData.costingMethod ===
                              CostingMethodEnum.UNIT_COST
                              ? "bg-background shadow-sm text-foreground"
                              : "text-muted-foreground hover:text-foreground",
                          )}
                          onClick={() => {
                            if (
                              formData.costingMethod ===
                              CostingMethodEnum.UNIT_COST
                            ) {
                              return;
                            }
                            bomMarginsStashRef.current =
                              formData.margin?.filter(
                                (m) => m.type !== MarginTypeEnum.all,
                              ) || [];
                            const stashed = unitCostStashRef.current;
                            const restoredCostBreaks =
                              formData.costBreaks.length > 0
                                ? formData.costBreaks
                                : stashed?.costBreaks?.length
                                  ? stashed.costBreaks
                                  : [
                                      {
                                        id: createId(),
                                        itemId: item?.id || "",
                                        minQty: 1,
                                        unitCost: "",
                                      },
                                    ];
                            const currentAllMargins =
                              formData.margin?.filter(
                                (m) => m.type === MarginTypeEnum.all,
                              ) || [];
                            const restoredItemMargins =
                              stashed?.itemMargins ?? currentAllMargins;
                            setFormData({
                              ...formData,
                              costingMethod: CostingMethodEnum.UNIT_COST,
                              costBreaks: restoredCostBreaks,
                              margin: restoredItemMargins,
                              priceModelId: null,
                              priceModelInputs: undefined,
                            });
                            setEnableCostBreaks(
                              stashed?.enableCostBreaks ?? enableCostBreaks,
                            );
                            setEnableItemMargins(
                              stashed?.enableItemMargins ??
                                currentAllMargins.length > 0,
                            );
                            resetPriceModelInputs({});
                          }}
                        >
                          Unit Cost
                        </button>
                        <button
                          type="button"
                          className={cn(
                            "flex-1 px-3 py-1.5 text-sm font-medium rounded transition-all duration-200",
                            formData.costingMethod === CostingMethodEnum.BOM
                              ? "bg-background shadow-sm text-foreground"
                              : "text-muted-foreground hover:text-foreground",
                          )}
                          onClick={() => {
                            if (
                              formData.costingMethod === CostingMethodEnum.BOM
                            ) {
                              return;
                            }
                            unitCostStashRef.current = {
                              costBreaks: formData.costBreaks,
                              itemMargins:
                                formData.margin?.filter(
                                  (m) => m.type === MarginTypeEnum.all,
                                ) || [],
                              enableCostBreaks,
                              enableItemMargins,
                            };
                            const restoredBOMMargins =
                              bomMarginsStashRef.current ?? [];
                            const currentItemMargins =
                              formData.margin?.filter(
                                (m) => m.type === MarginTypeEnum.all,
                              ) || [];
                            setFormData({
                              ...formData,
                              costingMethod: CostingMethodEnum.BOM,
                              margin: [
                                ...restoredBOMMargins,
                                ...currentItemMargins,
                              ],
                            });
                          }}
                        >
                          BOM
                        </button>
                      </div>
                    </FormRow>
                    <FormRow anchor="unit" label="Unit" htmlFor="unit" required>
                      <Select
                        disabled={isReadOnly}
                        value={formData.unit}
                        onValueChange={(value: ItemUnit) =>
                          setFormData({
                            ...formData,
                            unit: value,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {itemUnitOptions.map((unit) => (
                            <SelectItem key={unit} value={unit}>
                              {ITEM_UNIT_LABEL[unit]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormRow>
                    <FormRow
                      anchor="weightUnit"
                      label="Weight Unit"
                      htmlFor="weightUnit"
                    >
                      <Select
                        disabled={isReadOnly}
                        value={formData.weightUnit ?? "none"}
                        onValueChange={(value: string) => {
                          const weightUnit =
                            value === "none" ? null : (value as WeightUnit);
                          setFormData({
                            ...formData,
                            weightUnit,
                            weight: weightUnit ? formData.weight : null,
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value={WeightUnitEnum.kg}>
                            Kilograms (kg)
                          </SelectItem>
                          <SelectItem value={WeightUnitEnum.pounds}>
                            Pounds (lb)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormRow>
                    {formData.weightUnit && (
                      <FormRow
                        anchor="weight"
                        label="Weight per Each"
                        htmlFor="weight"
                      >
                        <div className="relative">
                          <Input
                            id="weight"
                            type="text"
                            inputMode="decimal"
                            placeholder="Weight per each"
                            value={formData.weight ?? ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (
                                val === "" ||
                                /^(\d+\.?\d*|\.\d+)$/.test(val)
                              ) {
                                setFormData({
                                  ...formData,
                                  weight: val || null,
                                });
                              }
                            }}
                            className="pr-14"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                            {WEIGHT_UNIT_ABBREVIATION[formData.weightUnit]}
                          </span>
                        </div>
                      </FormRow>
                    )}
                    <FormRow
                      anchor="dimensionUnit"
                      label="Dimension Unit"
                      htmlFor="dimensionUnit"
                    >
                      <Select
                        disabled={isReadOnly}
                        value={formData.dimensionUnit ?? "none"}
                        onValueChange={(value: string) => {
                          const dimensionUnit =
                            value === "none" ? null : (value as DimensionUnit);
                          setFormData({
                            ...formData,
                            dimensionUnit,
                            length: dimensionUnit ? formData.length : null,
                            width: dimensionUnit ? formData.width : null,
                            height: dimensionUnit ? formData.height : null,
                            allowance: dimensionUnit
                              ? formData.allowance
                              : null,
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {DimensionUnitSchema.options.map((unit) => (
                            <SelectItem key={unit} value={unit}>
                              {DIMENSION_UNIT_LABEL[unit]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormRow>
                    {formData.dimensionUnit && (
                      <>
                        <FormRow
                          anchor="length"
                          label="Length per Each"
                          htmlFor="length"
                        >
                          <div className="relative">
                            <Input
                              id="length"
                              type="text"
                              inputMode="decimal"
                              placeholder="Length"
                              value={formData.length ?? ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (
                                  val === "" ||
                                  /^(\d+\.?\d*|\.\d+)$/.test(val)
                                ) {
                                  setFormData({
                                    ...formData,
                                    length: val || null,
                                  });
                                }
                              }}
                              className="pr-14"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                              {
                                DIMENSION_UNIT_ABBREVIATION[
                                  formData.dimensionUnit
                                ]
                              }
                            </span>
                          </div>
                        </FormRow>
                        <FormRow
                          anchor="width"
                          label="Width per Each"
                          htmlFor="width"
                        >
                          <div className="relative">
                            <Input
                              id="width"
                              type="text"
                              inputMode="decimal"
                              placeholder="Width"
                              value={formData.width ?? ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (
                                  val === "" ||
                                  /^(\d+\.?\d*|\.\d+)$/.test(val)
                                ) {
                                  setFormData({
                                    ...formData,
                                    width: val || null,
                                  });
                                }
                              }}
                              className="pr-14"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                              {
                                DIMENSION_UNIT_ABBREVIATION[
                                  formData.dimensionUnit
                                ]
                              }
                            </span>
                          </div>
                        </FormRow>
                        <FormRow
                          anchor="height"
                          label="Height per Each"
                          htmlFor="height"
                        >
                          <div className="relative">
                            <Input
                              id="height"
                              type="text"
                              inputMode="decimal"
                              placeholder="Height"
                              value={formData.height ?? ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (
                                  val === "" ||
                                  /^(\d+\.?\d*|\.\d+)$/.test(val)
                                ) {
                                  setFormData({
                                    ...formData,
                                    height: val || null,
                                  });
                                }
                              }}
                              className="pr-14"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                              {
                                DIMENSION_UNIT_ABBREVIATION[
                                  formData.dimensionUnit
                                ]
                              }
                            </span>
                          </div>
                        </FormRow>
                        {enableAllowance && (
                          <FormRow
                            anchor="allowance"
                            label="Allowance"
                            htmlFor="allowance"
                          >
                            <div className="relative">
                              <Input
                                id="allowance"
                                type="text"
                                inputMode="decimal"
                                placeholder="Allowance"
                                value={formData.allowance ?? ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (
                                    val === "" ||
                                    /^(\d+\.?\d*|\.\d+)$/.test(val)
                                  ) {
                                    setFormData({
                                      ...formData,
                                      allowance: val || null,
                                    });
                                  }
                                }}
                                className="pr-14"
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                                {
                                  DIMENSION_UNIT_ABBREVIATION[
                                    formData.dimensionUnit
                                  ]
                                }
                              </span>
                            </div>
                          </FormRow>
                        )}
                      </>
                    )}
                    {itemUnitConversionsSection}
                  </div>

                  {itemCustomFieldsSection}

                  {!hasPriceModel && (
                    <ItemCostBreak
                      itemUnit={formData.unit}
                      itemId={item?.id}
                      costBreaks={formData.costBreaks}
                      enableCostBreaks={enableCostBreaks}
                      onCostBreaksChange={(costBreaks) =>
                        setFormData({ ...formData, costBreaks })
                      }
                      onEnableCostBreaksChange={setEnableCostBreaks}
                      switchId="enable-cost-breaks"
                      costInputId="simple-cost"
                    />
                  )}
                </div>
              </fieldset>
            </TabsContent>

            <TabsContent value="bom" className="mt-4">
              <fieldset disabled={isReadOnly} className="min-w-0 border-0 p-0">
                {(priceModelsList.length > 0 || linkedPriceModel) && (
                  <div className="space-y-6 py-2 border-b mb-6">
                    <FormRow
                      label="Price Model"
                      htmlFor="edit-price-model-select"
                      wide
                    >
                      <Select
                        disabled={isReadOnly}
                        value={formData.priceModelId || "none"}
                        onValueChange={(value) => {
                          if (value === "none") {
                            setFormData({
                              ...formData,
                              priceModelId: null,
                              priceModelInputs: undefined,
                            });
                            resetPriceModelInputs({});
                            return;
                          }
                          const pm = priceModelsMap.get(value);
                          setFormData({
                            ...formData,
                            priceModelId: value,
                          });
                          resetPriceModelInputs(
                            pm ? getEffectiveDefaults(pm) : {},
                          );
                        }}
                      >
                        <SelectTrigger id="edit-price-model-select">
                          <SelectValue placeholder="Manual item (no price model)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            Manual item (no price model)
                          </SelectItem>
                          {linkedPriceModel?.archivedAt && (
                            <SelectItem value={linkedPriceModel.id}>
                              {linkedPriceModel.name} (archived)
                            </SelectItem>
                          )}
                          {priceModelsList.map((pm) => (
                            <SelectItem key={pm.id} value={pm.id}>
                              {pm.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormRow>

                    {formData.priceModelId && (
                      <>
                        <div className="space-y-2">
                          <h4 className="border-b border-border/80 pb-1.5 text-xs font-bold uppercase tracking-wider text-foreground/70">
                            Price Model Inputs
                          </h4>
                          {priceModelInputSchema ? (
                            <JSONSchemaForm
                              disabled={isReadOnly}
                              schema={priceModelInputSchema}
                              formData={priceModelFormData}
                              onChange={handlePriceModelInputsChange}
                              showSubmitButton={false}
                              numberFields
                              modifiedFields={modifiedFields}
                              fieldTemplate={PriceModelInputFieldTemplate}
                              formContext={priceModelInputFormContext}
                            />
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              No input variables defined for this price model.
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
                {formData.priceModelId && item && (
                  <PriceModelSummary
                    item={item}
                    quantities={quantities}
                    costs={priceModelCosts}
                    loading={priceModelCostsLoading}
                    priceModelId={formData.priceModelId}
                    calculationFields={calculationFields.map((f) => ({
                      key: f.key,
                      label: f.label,
                      suffix: f.suffix,
                    }))}
                  />
                )}
              </fieldset>
              {item && (
                <BOMWrapper
                  itemId={item.id}
                  quantities={quantities}
                  onUpdated={onBomUpdated}
                  onNavigateToItem={
                    isAssembly ? setSelectedTreeItemId : undefined
                  }
                  locked={isReadOnly}
                  currencyContext={currencyContext}
                />
              )}
              {removedRowNote && (
                <p className="px-2 pt-2 text-xs text-muted-foreground">
                  {removedRowNote}
                </p>
              )}
            </TabsContent>
          </Tabs>
        </>
      );
    }

    return (
      <>
        <fieldset
          disabled={isReadOnly}
          className="min-w-0 space-y-4 border-0 p-0"
        >
          {mode === "create" && priceModelsList.length > 0 && (
            <FormRow
              label="Create from Price Model"
              htmlFor="price-model-select"
              wide
            >
              <Select
                disabled={isReadOnly}
                value={selectedPriceModelId || "none"}
                onValueChange={(value) => {
                  if (value === "none") {
                    setSelectedPriceModelId(null);
                    resetPriceModelInputs({});
                    return;
                  }
                  setSelectedPriceModelId(value);
                  const pm = priceModelsMap.get(value);
                  resetPriceModelInputs(pm ? getEffectiveDefaults(pm) : {});
                }}
              >
                <SelectTrigger id="price-model-select">
                  <SelectValue placeholder="Manual item (no price model)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    Manual item (no price model)
                  </SelectItem>
                  {priceModelsList.map((pm) => (
                    <SelectItem key={pm.id} value={pm.id}>
                      {pm.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormRow>
          )}

          {mode === "create" && selectedPriceModelId && (
            <div className="space-y-2">
              <h4 className="border-b border-border/80 pb-1.5 text-xs font-bold uppercase tracking-wider text-foreground/70">
                Price Model Inputs
              </h4>
              {priceModelInputSchema ? (
                <JSONSchemaForm
                  disabled={isReadOnly}
                  schema={priceModelInputSchema}
                  formData={priceModelFormData}
                  onChange={handlePriceModelInputsChange}
                  showSubmitButton={false}
                  numberFields
                  fieldTemplate={PriceModelInputFieldTemplate}
                  formContext={priceModelInputFormContext}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  No input variables defined for this price model.
                </p>
              )}
            </div>
          )}

          <div className="divide-y">
            <FormRow
              anchor="name"
              label="Name / Item Number"
              htmlFor="name"
              required
              wide
              note={partNumberLengthNote}
            >
              <Input
                id="name"
                value={formData.name}
                maxLength={maxPartNumberLength ?? undefined}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </FormRow>
            <FormRow anchor="revision" label="Revision" htmlFor="revision">
              <Input
                id="revision"
                value={formData.revision}
                onChange={(e) =>
                  setFormData({ ...formData, revision: e.target.value })
                }
              />
            </FormRow>
            <div className="py-2.5 space-y-1.5" data-field-anchor="description">
              <Label
                htmlFor="description"
                className="text-sm font-medium leading-tight"
              >
                Description
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="min-h-[80px]"
              />
            </div>

            {!selectedPriceModelId && (
              <>
                <FormRow anchor="type" label="Type" htmlFor="type" required>
                  <Select
                    disabled={isReadOnly}
                    value={formData.type}
                    onValueChange={(value: typeof formData.type) => {
                      const hasExistingContent =
                        formData.costBreaks.length > 0 ||
                        (item?.components?.length ?? 0) > 0 ||
                        (item?.operations?.length ?? 0) > 0;
                      const newCostingMethod = hasExistingContent
                        ? formData.costingMethod
                        : getDefaultCostingMethod(value);
                      setFormData({
                        ...formData,
                        type: value,
                        costingMethod: newCostingMethod,
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ItemTypeEnum.MANUFACTURED}>
                        Manufactured
                      </SelectItem>
                      <SelectItem value={ItemTypeEnum.PURCHASED}>
                        Purchased
                      </SelectItem>
                      <SelectItem value={ItemTypeEnum.MATERIAL}>
                        Material
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormRow>
                <FormRow
                  anchor="costingMethod"
                  label="Costing Method"
                  note={
                    showBOMSaveHint ? (
                      <div className="mt-1.5">
                        <p className="flex items-center gap-1.5 text-xs text-muted-foreground animate-in fade-in slide-in-from-top-1 duration-200">
                          <Info className="h-3 w-3 shrink-0" />
                          Save item to view BOM & Pricing
                        </p>
                      </div>
                    ) : undefined
                  }
                >
                  <div className="flex gap-1 p-1 bg-muted rounded-md">
                    <button
                      type="button"
                      className={cn(
                        "flex-1 px-3 py-1.5 text-sm font-medium rounded transition-all duration-200",
                        formData.costingMethod === CostingMethodEnum.UNIT_COST
                          ? "bg-background shadow-sm text-foreground"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                      onClick={() => {
                        if (
                          formData.costingMethod === CostingMethodEnum.UNIT_COST
                        ) {
                          return;
                        }
                        bomMarginsStashRef.current =
                          formData.margin?.filter(
                            (m) => m.type !== MarginTypeEnum.all,
                          ) || [];
                        const stashed = unitCostStashRef.current;
                        const restoredCostBreaks =
                          formData.costBreaks.length > 0
                            ? formData.costBreaks
                            : stashed?.costBreaks?.length
                              ? stashed.costBreaks
                              : [
                                  {
                                    id: createId(),
                                    itemId: item?.id || "",
                                    minQty: 1,
                                    unitCost: "",
                                  },
                                ];
                        const currentAllMargins =
                          formData.margin?.filter(
                            (m) => m.type === MarginTypeEnum.all,
                          ) || [];
                        const restoredItemMargins =
                          stashed?.itemMargins ?? currentAllMargins;
                        setFormData({
                          ...formData,
                          costingMethod: CostingMethodEnum.UNIT_COST,
                          costBreaks: restoredCostBreaks,
                          margin: restoredItemMargins,
                          priceModelId: null,
                          priceModelInputs: undefined,
                        });
                        setEnableCostBreaks(
                          stashed?.enableCostBreaks ?? enableCostBreaks,
                        );
                        setEnableItemMargins(
                          stashed?.enableItemMargins ??
                            currentAllMargins.length > 0,
                        );
                        resetPriceModelInputs({});
                      }}
                    >
                      Unit Cost
                    </button>
                    <button
                      type="button"
                      className={cn(
                        "flex-1 px-3 py-1.5 text-sm font-medium rounded transition-all duration-200",
                        formData.costingMethod === CostingMethodEnum.BOM
                          ? "bg-background shadow-sm text-foreground"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                      onClick={() => {
                        if (formData.costingMethod === CostingMethodEnum.BOM) {
                          return;
                        }
                        unitCostStashRef.current = {
                          costBreaks: formData.costBreaks,
                          itemMargins:
                            formData.margin?.filter(
                              (m) => m.type === MarginTypeEnum.all,
                            ) || [],
                          enableCostBreaks,
                          enableItemMargins,
                        };
                        const restoredBOMMargins =
                          bomMarginsStashRef.current ?? [];
                        const currentItemMargins =
                          formData.margin?.filter(
                            (m) => m.type === MarginTypeEnum.all,
                          ) || [];
                        setFormData({
                          ...formData,
                          costingMethod: CostingMethodEnum.BOM,
                          margin: [
                            ...restoredBOMMargins,
                            ...currentItemMargins,
                          ],
                        });
                      }}
                    >
                      BOM
                    </button>
                  </div>
                </FormRow>
                <FormRow anchor="unit" label="Unit" htmlFor="unit" required>
                  <Select
                    disabled={isReadOnly}
                    value={formData.unit}
                    onValueChange={(value: ItemUnit) =>
                      setFormData({
                        ...formData,
                        unit: value,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {itemUnitOptions.map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {ITEM_UNIT_LABEL[unit]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormRow>
                <FormRow
                  anchor="weightUnit"
                  label="Weight Unit"
                  htmlFor="weightUnit"
                >
                  <Select
                    disabled={isReadOnly}
                    value={formData.weightUnit ?? "none"}
                    onValueChange={(value: string) => {
                      const weightUnit =
                        value === "none" ? null : (value as WeightUnit);
                      setFormData({
                        ...formData,
                        weightUnit,
                        weight: weightUnit ? formData.weight : null,
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value={WeightUnitEnum.kg}>
                        Kilograms (kg)
                      </SelectItem>
                      <SelectItem value={WeightUnitEnum.pounds}>
                        Pounds (lb)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormRow>
                {formData.weightUnit && (
                  <FormRow
                    anchor="weight"
                    label="Weight per Each"
                    htmlFor="weight"
                  >
                    <div className="relative">
                      <Input
                        id="weight"
                        type="text"
                        inputMode="decimal"
                        placeholder="Weight per each"
                        value={formData.weight ?? ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || /^(\d+\.?\d*|\.\d+)$/.test(val)) {
                            setFormData({
                              ...formData,
                              weight: val || null,
                            });
                          }
                        }}
                        className="pr-14"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                        {WEIGHT_UNIT_ABBREVIATION[formData.weightUnit]}
                      </span>
                    </div>
                  </FormRow>
                )}
                <FormRow
                  anchor="dimensionUnit"
                  label="Dimension Unit"
                  htmlFor="dimensionUnit"
                >
                  <Select
                    disabled={isReadOnly}
                    value={formData.dimensionUnit ?? "none"}
                    onValueChange={(value: string) => {
                      const dimensionUnit =
                        value === "none" ? null : (value as DimensionUnit);
                      setFormData({
                        ...formData,
                        dimensionUnit,
                        length: dimensionUnit ? formData.length : null,
                        width: dimensionUnit ? formData.width : null,
                        height: dimensionUnit ? formData.height : null,
                        allowance: dimensionUnit ? formData.allowance : null,
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {DimensionUnitSchema.options.map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {DIMENSION_UNIT_LABEL[unit]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormRow>
                {formData.dimensionUnit && (
                  <>
                    <FormRow
                      anchor="length"
                      label="Length per Each"
                      htmlFor="length"
                    >
                      <div className="relative">
                        <Input
                          id="length"
                          type="text"
                          inputMode="decimal"
                          placeholder="Length"
                          value={formData.length ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "" || /^(\d+\.?\d*|\.\d+)$/.test(val)) {
                              setFormData({
                                ...formData,
                                length: val || null,
                              });
                            }
                          }}
                          className="pr-14"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                          {DIMENSION_UNIT_ABBREVIATION[formData.dimensionUnit]}
                        </span>
                      </div>
                    </FormRow>
                    <FormRow
                      anchor="width"
                      label="Width per Each"
                      htmlFor="width"
                    >
                      <div className="relative">
                        <Input
                          id="width"
                          type="text"
                          inputMode="decimal"
                          placeholder="Width"
                          value={formData.width ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "" || /^(\d+\.?\d*|\.\d+)$/.test(val)) {
                              setFormData({
                                ...formData,
                                width: val || null,
                              });
                            }
                          }}
                          className="pr-14"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                          {DIMENSION_UNIT_ABBREVIATION[formData.dimensionUnit]}
                        </span>
                      </div>
                    </FormRow>
                    <FormRow
                      anchor="height"
                      label="Height per Each"
                      htmlFor="height"
                    >
                      <div className="relative">
                        <Input
                          id="height"
                          type="text"
                          inputMode="decimal"
                          placeholder="Height"
                          value={formData.height ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "" || /^(\d+\.?\d*|\.\d+)$/.test(val)) {
                              setFormData({
                                ...formData,
                                height: val || null,
                              });
                            }
                          }}
                          className="pr-14"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                          {DIMENSION_UNIT_ABBREVIATION[formData.dimensionUnit]}
                        </span>
                      </div>
                    </FormRow>
                    {enableAllowance && (
                      <FormRow
                        anchor="allowance"
                        label="Allowance"
                        htmlFor="allowance"
                      >
                        <div className="relative">
                          <Input
                            id="allowance"
                            type="text"
                            inputMode="decimal"
                            placeholder="Allowance"
                            value={formData.allowance ?? ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (
                                val === "" ||
                                /^(\d+\.?\d*|\.\d+)$/.test(val)
                              ) {
                                setFormData({
                                  ...formData,
                                  allowance: val || null,
                                });
                              }
                            }}
                            className="pr-14"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                            {
                              DIMENSION_UNIT_ABBREVIATION[
                                formData.dimensionUnit
                              ]
                            }
                          </span>
                        </div>
                      </FormRow>
                    )}
                  </>
                )}
                {itemUnitConversionsSection}
              </>
            )}
          </div>

          {itemCustomFieldsSection}

          {!selectedPriceModelId && (
            <ItemCostBreak
              itemUnit={formData.unit}
              itemId={item?.id}
              costBreaks={formData.costBreaks}
              enableCostBreaks={enableCostBreaks}
              onCostBreaksChange={(costBreaks) =>
                setFormData({ ...formData, costBreaks })
              }
              onEnableCostBreaksChange={setEnableCostBreaks}
              switchId="enable-cost-breaks-regular"
              costInputId="simple-cost-regular"
            />
          )}
        </fieldset>

        {!selectedPriceModelId && (
          <ItemMarginEditor
            costingMethod={formData.costingMethod}
            itemId={item?.id}
            margins={getItemMargins()}
            enableItemMargins={enableItemMargins}
            onMarginsChange={handleItemMarginsChange}
            onEnableItemMarginsChange={setEnableItemMargins}
            switchId="enable-item-margins-regular"
          />
        )}

        {mode === "edit" && itemId && <ItemAssociatedQuotes itemId={itemId} />}
      </>
    );
  };

  const rightColumn = fetchError ? null : (
    <div ref={rightColumnRef} className="h-full overflow-y-auto p-3 sm:p-6">
      {conflictItem && (
        <ItemEditorConflictBanner
          onLoadTheirChanges={handleLoadTheirChanges}
          onKeepMyChanges={handleKeepMyChanges}
        />
      )}

      {isChangedSinceQuoted && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <span className="font-medium">
              Changed since the quote was finalized
            </span>
            <span>
              {" "}
              - the values shown are the item&apos;s current values, not the
              quoted ones.
            </span>
          </div>
        </div>
      )}

      {isReadOnly && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <span className="font-medium">Synced with an integration</span>
            <span>
              {" "}
              &mdash; integration-controlled fields are read-only. Forgepoint
              margins and notes remain editable.
            </span>
            {lastIntegrationUpdate && (
              <span className="mt-1 block text-xs">
                Last integration update:{" "}
                <time dateTime={lastIntegrationUpdate.toISOString()}>
                  {lastIntegrationUpdate.toLocaleString(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </time>
              </span>
            )}
          </div>
        </div>
      )}
      {rightColumnContent()}
    </div>
  );

  // BOM margins are persisted by BOMMarginEditor, so the outer sheet has
  // nothing else to save for an integration-controlled BOM item.
  const footer =
    isReadOnly && formData.costingMethod === CostingMethodEnum.BOM ? (
      <div className="flex justify-end">
        <Button onClick={() => handleOpenChange(false)}>Close</Button>
      </div>
    ) : (
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => handleOpenChange(false)}
          disabled={isSaving}
        >
          Go back
        </Button>
        <Button
          onClick={() => handleSave()}
          disabled={isSaving || (mode === "edit" && !isDirty)}
        >
          {isSaving && <Loader className="h-4 w-4 mr-2 animate-spin" />}
          {mode === "create" ? "Create Item" : "Save Changes"}
          {mode === "edit" && modifiedFields.size > 0 && (
            <span className="ml-1.5 rounded-full bg-amber-500/20 px-1.5 text-xs font-medium text-amber-600">
              {modifiedFields.size}
            </span>
          )}
        </Button>
      </div>
    );

  const assemblyTreeSidebar =
    isAssembly && rootItem ? (
      <AssemblyTreeSidebar
        rootItem={rootItem}
        selectedItemId={selectedTreeItemId ?? rootItem.id}
        onSelectItem={setSelectedTreeItemId}
        onMarkReviewed={(id) => markItemsReviewed([id])}
      />
    ) : undefined;

  const layoutProps: Omit<TwoColumnLayoutProps, "onClose" | "autoSaveId"> = {
    header,
    leftColumn,
    rightColumn: fetchError ? null : rightColumn,
    footer: fetchError ? null : footer,
    sidePanel: assemblyTreeSidebar,
    leftColumnSize: fetchError ? "full" : "half",
    resizable: !fetchError,
    loading: isLoading,
    loadingText: "Loading item details...",
  };

  const closeConfirmDialog = (
    <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved edits on this item. Closing the editor will discard
            them.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep editing</AlertDialogCancel>
          <AlertDialogAction onClick={resetAndClose}>
            Discard changes
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  if (inline) {
    if (!open) {
      return null;
    }
    return (
      <>
        <TwoColumnLayout
          {...layoutProps}
          autoSaveId="item-editor-inline"
          onClose={() => handleOpenChange(false)}
        />
        {closeConfirmDialog}
      </>
    );
  }

  return (
    <>
      <TwoColumnSheet
        {...layoutProps}
        autoSaveId="item-editor-sheet"
        open={open}
        onOpenChange={handleOpenChange}
      />
      {closeConfirmDialog}
    </>
  );
}

// Matches the field-flash-highlight animation in index.css.
const FIELD_HIGHLIGHT_MS = 2000;

/**
 * Scrolls the field a History row points at into view and flashes it.
 * @param container - The form column to search
 * @param anchor - The anchor the change resolved to
 */
function highlightField(container: HTMLElement | null, anchor: string): void {
  const field = findAnchoredField(container, anchor);
  if (!field) {
    return;
  }
  field.scrollIntoView({ block: "center" });
  field.classList.add("field-flash-highlight");
  window.setTimeout(
    () => field.classList.remove("field-flash-highlight"),
    FIELD_HIGHLIGHT_MS,
  );
}

/** The anchored field, else its section when the row is gone. */
function findAnchoredField(
  container: HTMLElement | null,
  anchor: string,
): HTMLElement | null {
  if (!container) {
    return null;
  }
  for (const path of [anchor, anchor.split(".")[0]]) {
    const match = container.querySelector(`[data-field-anchor="${path}"]`);
    if (match instanceof HTMLElement) {
      return match;
    }
  }
  return null;
}

/** Recursively searches the component tree for an item by ID. */
function findItemInTree(
  root: HydratedItem,
  targetId: string,
): HydratedItem | null {
  if (root.id === targetId) {
    return root;
  }
  for (const comp of root.components ?? []) {
    const found = findItemInTree(comp.component, targetId);
    if (found) {
      return found;
    }
  }
  return null;
}

/** Builds the breadcrumb path from root to a target item in the component tree. */
function buildBreadcrumbPath(
  root: HydratedItem,
  targetId: string,
): Array<{ id: string; name: string }> {
  if (root.id === targetId) {
    return [{ id: root.id, name: root.name }];
  }
  for (const comp of root.components ?? []) {
    const childPath = buildBreadcrumbPath(comp.component, targetId);
    if (childPath.length > 0) {
      return [{ id: root.id, name: root.name }, ...childPath];
    }
  }
  return [];
}
